import useSWR from 'swr'
import { BareFetcher, PublicConfiguration } from 'swr/_internal'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { addQueryParam } from 'shared/utils/add-query-param'
import { MEMBERSHIP_API } from 'modules/school/api/membership-api'
import { CourseLectureDataInterface, CourseLectureId } from '../types/course-lecture-interface'

export interface UseCourseProps {
  courseLectureId: CourseLectureId | undefined
  searchQuery?: string
  shouldFetch?: boolean
  onError?: (
    err: Error,
    key?: string,
    config?: Readonly<
      PublicConfiguration<
        CourseLectureDataInterface,
        Error,
        BareFetcher<CourseLectureDataInterface>
      >
    >,
  ) => void
}

export const getCourseLectureKey = ({
  courseLectureId,
  searchQuery,
}: {
  courseLectureId?: CourseLectureId
  searchQuery?: string
}) => {
  if (courseLectureId === undefined) return null

  const searchParams = new URLSearchParams()
  addQueryParam(searchParams, 'searchBy', searchQuery)

  const queryString = searchParams.toString()
  const baseUrl = `${MEMBERSHIP_API}/lecture/${courseLectureId}`

  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

export function useCourseLecture({
  courseLectureId,
  searchQuery,
  shouldFetch = true,
  onError,
}: UseCourseProps) {
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    CourseLectureDataInterface
  >({
    method: RequestMethodsEnum.get,
    accessDeniedResponseHandler: error => onError && onError(error),
  })

  const {
    data: courseLecture,
    mutate,
    isValidating,
  } = useSWR<CourseLectureDataInterface>(
    () => shouldFetch && getCourseLectureKey({ courseLectureId, searchQuery }),
    fetcher,
    { onError, shouldRetryOnError: false },
  )

  return { courseLecture, mutate, isValidating }
}
